import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { KeyRound, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ResetPassword() {
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const token = params.get("token") || "";
  const exp = params.get("exp") || "";
  const navigate = useNavigate();

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const missing = !email || !token || !exp;

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password?exp=${encodeURIComponent(exp)}`,
        { email, token, new_password: pw }, { withCredentials: true });
      toast.success("Password updated — you're signed in");
      navigate("/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset link is invalid or expired");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary mb-6" data-testid="back-to-login">
          <ArrowLeft size={12} /> Back to sign in
        </Link>
        <div className="border border-border rounded-lg bg-card p-8">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound size={16} className="text-primary" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold">Password reset</div>
          </div>
          <h1 className="heading text-2xl mb-2">Set a new password</h1>
          {missing ? (
            <p className="text-sm text-destructive" data-testid="reset-link-invalid">This reset link is missing information. Request a fresh one from the sign-in screen.</p>
          ) : (
            <form onSubmit={submit}>
              <p className="text-sm text-muted-foreground mb-6">You're resetting the password for <span className="font-medium text-foreground">{email}</span>.</p>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="pw">New password</Label>
                  <Input id="pw" type="password" autoComplete="new-password" minLength={8} required
                         value={pw} onChange={(e) => setPw(e.target.value)}
                         data-testid="reset-new-input" />
                </div>
                <div>
                  <Label htmlFor="confirm">Confirm password</Label>
                  <Input id="confirm" type="password" autoComplete="new-password" minLength={8} required
                         value={confirm} onChange={(e) => setConfirm(e.target.value)}
                         data-testid="reset-confirm-input" />
                </div>
                <Button type="submit" className="w-full" disabled={busy || !pw || !confirm} data-testid="reset-submit-btn">
                  {busy ? "Updating…" : "Set new password"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
